import { useEffect, useMemo, useState, useCallback } from "react";
import {
    Box,
    Heading,
    Table,
    Thead,
    Tbody,
    Tr,
    Th,
    Td,
    TableContainer,
    Input,
    InputGroup,
    InputLeftElement,
    Button,
    HStack,
    Badge,
    Spinner,
    Center,
    Text,
    Select,
    useDisclosure,
    Modal,
    ModalOverlay,
    ModalContent,
    ModalHeader,
    ModalCloseButton,
    ModalBody,
    ModalFooter,
} from "@chakra-ui/react";

import {
    SearchIcon,
    AddIcon,
    DownloadIcon,
    ChevronLeftIcon,
    ChevronRightIcon,
    ViewIcon,
} from "@chakra-ui/icons";
import LabUjiService from "services/report/ReportLabService.js";
import { flattenLaporan, formatTgl } from "utils/LaporanUtils.js";
import TambahPengujianModal from "./ModalReport.js";
import ExportExcelModal from "./ExportExcel.js";
import Card from "components/Card/Card.js";

const KATEGORI_COLOR = {
    Biologi: "green",
    Kimia: "orange",
    Fisika: "purple",
};

export default function LaporanPengujianTable() {
    const [headerRows, setHeaderRows] = useState([]);
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState("");

    // State Pagination
    const [page, setPage] = useState(1);
    const [limit, setLimit] = useState(10);
    const [pagination, setPagination] = useState({
        totalItems: 0,
        totalPages: 1,
        currentPage: 1,
        limit: 10,
    });

    const addModal = useDisclosure();
    const exportModal = useDisclosure();

    const documentModal = useDisclosure();
    const [selectedDocument, setSelectedDocument] = useState(null);

    // Fetch Data dengan Pagination Backend
    const [excelRows, setExcelRows] = useState([]);

    // Fetch Data dengan Pagination Backend
    const loadData = useCallback(async () => {
    setLoading(true);
    try {
        const res = await LabUjiService.getAllLabUji({ page, limit });
        const responseData = res?.data?.data ? res.data : res;

        let rawRows = [];
        if (Array.isArray(responseData.data)) {
            rawRows = responseData.data;
        } else if (Array.isArray(responseData)) {
            rawRows = responseData;
        } else if (typeof responseData === "object" && responseData !== null) {
            rawRows = Object.keys(responseData)
                .filter((key) => !isNaN(key))
                .map((key) => responseData[key]);
        }

        setHeaderRows(rawRows);

        if (responseData.pagination) {
            setPagination(responseData.pagination);
        } else if (res.pagination) {
            setPagination(res.pagination);
        }

        // Ambil SEMUA data untuk Rekap Excel tanpa terpotong limit pagination
        try {
            const resAll = await LabUjiService.getAllLabUji({ page: 1, limit: 10000 });
            const allDataResp = resAll?.data?.data ? resAll.data : resAll;
            let rawAllRows = [];
            if (Array.isArray(allDataResp.data)) {
                rawAllRows = allDataResp.data;
            } else if (Array.isArray(allDataResp)) {
                rawAllRows = allDataResp;
            } else if (typeof allDataResp === "object" && allDataResp !== null) {
                rawAllRows = Object.keys(allDataResp)
                    .filter((key) => !isNaN(key))
                    .map((key) => allDataResp[key]);
            }
            setExcelRows(flattenLaporan(rawAllRows));
        } catch (e) {
            setExcelRows(flattenLaporan(rawRows));
        }

    } catch (err) {
        console.error("Gagal memuat data pengujian:", err);
        setHeaderRows([]);
        setExcelRows([]);
    } finally {
        setLoading(false);
    }
}, [page, limit]);

    const allFlatRows = useMemo(() => flattenLaporan(headerRows), [headerRows]);
    const finalExcelRows = useMemo(() => (excelRows.length > 0 ? excelRows : allFlatRows), [excelRows, allFlatRows]);

    useEffect(() => {
        loadData();
    }, [loadData]);

    // Pencarian Client-Side pada Data Terpaginasi
    const filteredHeaderRows = useMemo(() => {
        if (!search.trim()) return headerRows;
        const q = search.toLowerCase();
        return headerRows.filter((h) => {
            const haystack = [
                h.nm_upi,
                h.nm_jenis_uji,
                h.tmp_uji,
                ...(h.jenis_uji || []).map(
                    (d) => `${d.nm_uji} ${d.keterangan} ${d.result}`
                ),
            ]
                .join(" ")
                .toLowerCase();
            return haystack.includes(q);
        });
    }, [headerRows, search]);

    const flatRows = useMemo(() => flattenLaporan(filteredHeaderRows), [filteredHeaderRows]);

    const handleLimitChange = (e) => {
        setLimit(Number(e.target.value));
        setPage(1); // Reset ke halaman 1 setiap ganti limit
    };

    const getDocumentUrl = (dokpath) => {
        if (!dokpath) return null;

        const baseUrl = process.env.REACT_APP_API_FILE || "";

        return `${baseUrl.replace(/\/$/, "")}/${dokpath.replace(/^\//, "")}`;
    };

    const handleViewDocument = (dokpath) => {
        if (!dokpath) return;

        setSelectedDocument(getDocumentUrl(dokpath));
        documentModal.onOpen();
    };

    return (
        <Box p={{ base: 4, md: 8 }} bg="gray.50" minH="100vh">
            <Card overflowX={{ sm: "scroll", xl: "hidden" }} mb={4} mt={4}>
                <HStack justify="space-between" flexWrap="wrap" gap={3}>
                    <Heading size="lg">Laporan Pengujian</Heading>
                    <HStack>
                        <Button
                            leftIcon={<DownloadIcon />}
                            variant="outline"
                            colorScheme="green"
                            onClick={exportModal.onOpen}
                            isDisabled={allFlatRows.length === 0}
                        >
                            Rekap Excel
                        </Button>
                        <Button leftIcon={<AddIcon />} colorScheme="blue" onClick={addModal.onOpen}>
                            Tambah Data
                        </Button>
                    </HStack>
                </HStack>
            </Card>

            <HStack justify="space-between" mb={4} flexWrap="wrap" gap={3}>
                <InputGroup maxW="420px" bg="white">
                    <InputLeftElement pointerEvents="none">
                        <SearchIcon color="gray.400" />
                    </InputLeftElement>
                    <Input
                        placeholder="Cari nama UPI, produk, atau parameter uji..."
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                    />
                </InputGroup>

                <HStack spacing={2}>
                    <Text fontSize="sm" whiteSpace="nowrap">Tampilkan:</Text>
                    <Select size="sm" value={limit} onChange={handleLimitChange} bg="white" w="90px">
                        <option value={10}>10</option>
                        <option value={30}>30</option>
                        <option value={50}>50</option>
                        <option value={100}>100</option>
                    </Select>
                </HStack>
            </HStack>

            <TableContainer bg="white" borderRadius="md" borderWidth="1px" boxShadow="sm">
                <Table size="sm" variant="simple">
                    <Thead bg="gray.100">
                        <Tr>
                            <Th>No ID</Th>
                            <Th>Nama UPI</Th>
                            <Th>Nama Produk</Th>
                            <Th>Tgl Pengambilan Contoh</Th>
                            <Th>Tgl Contoh Diterima Lab</Th>
                            <Th>Tgl Terbit LHU</Th>
                            <Th>Kategori</Th>
                            <Th>Parameter Uji</Th>
                            <Th isNumeric>Hasil Uji</Th>
                            <Th>Satuan</Th>
                            <Th>Metode Uji</Th>
                            <Th>LOD/LOQ</Th>
                            <Th>Keterangan</Th>
                            <Th>Tempat Uji</Th>
                            <Th textAlign="center">Dokumen</Th>
                        </Tr>
                    </Thead>
                    <Tbody>
                        {flatRows.map((r) => (
                            <Tr key={r.key}>
                            {/* Kolom Induk / Header yang di-rowSpan */}
                            {r.isFirstOfHeader && (
                                <>
                                 <Td rowSpan={r.headerRowSpan} fontWeight="medium" verticalAlign="top">
                                    {r.id_lab || "-"}
                                </Td>
                                <Td rowSpan={r.headerRowSpan} fontWeight="medium" verticalAlign="top">
                                    {r.nm_upi || "-"}
                                </Td>
                                <Td rowSpan={r.headerRowSpan} verticalAlign="top">
                                    {/* Tampilkan Nama Produk / Jenis Uji */}
                                    {r.nm_jenis_uji || "-"}
                                </Td>
                                <Td rowSpan={r.headerRowSpan} verticalAlign="top">
                                    {formatTgl(r.tgl_contoh)}
                                </Td>
                                <Td rowSpan={r.headerRowSpan} verticalAlign="top">
                                    {formatTgl(r.tgl_sur)}
                                </Td>
                                <Td rowSpan={r.headerRowSpan} verticalAlign="top">
                                    {formatTgl(r.tgl_lhu)}
                                </Td>
                                </>
                            )}

                            {/* Kolom Kategori (jika tidak ada rowspan kategori terpisah) */}
                            <Td verticalAlign="top">
                                <Badge colorScheme={KATEGORI_COLOR[r.kategori] || "gray"}>
                                {r.kategori || "-"}
                                </Badge>
                            </Td>

                            {/* Kolom Detail Uji */}
                            <Td>{r.nm_uji || "-"}</Td>
                            <Td isNumeric>{r.result || "-"}</Td>
                            <Td>{r.satuan || "-"}</Td>
                            <Td>{r.methode || "-"}</Td>
                            <Td>{r.lod_loq || "-"}</Td>
                            <Td fontWeight="semibold">{r.keterangan || "-"}</Td>

                            {/* Tempat Uji (Induk) */}
                            {r.isFirstOfHeader && (
                                <Td rowSpan={r.headerRowSpan} verticalAlign="top" fontSize="xs" color="gray.600">
                                {r.tmp_uji || "-"}
                                </Td>
                            )}
                            {r.isFirstOfHeader && (
                                    <Td
                                        rowSpan={r.headerRowSpan}
                                        verticalAlign="top"
                                        textAlign="center"
                                    >
                                        {r.dokpath ? (
                                            <Button
                                                size="xs"
                                                colorScheme="blue"
                                                variant="outline"
                                                leftIcon={<ViewIcon />}
                                                onClick={() => handleViewDocument(r.dokpath)}
                                            >
                                                Lihat
                                            </Button>
                                        ) : (
                                            <Text fontSize="xs" color="gray.400">
                                                Tidak ada
                                            </Text>
                                        )}
                                    </Td>
                                )}
                            </Tr>
                        ))}
                        </Tbody>
                </Table>

                {loading && (
                    <Center py={10}>
                        <Spinner />
                    </Center>
                )}
                {!loading && flatRows.length === 0 && (
                    <Center py={10}>
                        <Text color="gray.500">Tidak ada data ditemukan.</Text>
                    </Center>
                )}
            </TableContainer>

            {/* Footer Control Pagination */}
            <HStack justify="space-between" mt={4} px={2}>
                <Text fontSize="sm" color="gray.600">
                    Menampilkan {flatRows.length} baris | Total {pagination.totalItems || 0} Data Utama
                </Text>
                <HStack spacing={2}>
                    <Button
                        size="sm"
                        leftIcon={<ChevronLeftIcon />}
                        onClick={() => setPage((p) => Math.max(p - 1, 1))}
                        isDisabled={page === 1 || loading}
                    >
                        Prev
                    </Button>
                    <Text fontSize="sm" fontWeight="bold">
                        {pagination.currentPage || page} / {pagination.totalPages || 1}
                    </Text>
                    <Button
                        size="sm"
                        rightIcon={<ChevronRightIcon />}
                        onClick={() => setPage((p) => Math.min(p + 1, pagination.totalPages || 1))}
                        isDisabled={page >= (pagination.totalPages || 1) || loading}
                    >
                        Next
                    </Button>
                </HStack>
            </HStack>

            <TambahPengujianModal
                isOpen={addModal.isOpen}
                onClose={addModal.onClose}
                onCreated={loadData}
            />
            <ExportExcelModal
                isOpen={exportModal.isOpen}
                onClose={exportModal.onClose}
                allFlatRows={finalExcelRows}
            />
            <Modal
                isOpen={documentModal.isOpen}
                onClose={() => {
                    documentModal.onClose();
                    setSelectedDocument(null);
                }}
                size="6xl"
                isCentered
            >
                <ModalOverlay bg="blackAlpha.700" />

                <ModalContent maxH="95vh">
                    <ModalHeader>Dokumen Pengujian</ModalHeader>

                    <ModalCloseButton />

                    <ModalBody p={0}>
                        {selectedDocument && (
                            <Box
                                as="iframe"
                                src={selectedDocument}
                                width="100%"
                                height="75vh"
                                border="0"
                                title="Dokumen Pengujian"
                            />
                        )}
                    </ModalBody>

                    <ModalFooter>
                        {selectedDocument && (
                            <Button
                                as="a"
                                href={selectedDocument}
                                target="_blank"
                                rel="noopener noreferrer"
                                colorScheme="blue"
                                mr={3}
                            >
                                Buka Tab Baru
                            </Button>
                        )}

                        <Button
                            variant="ghost"
                            onClick={() => {
                                documentModal.onClose();
                                setSelectedDocument(null);
                            }}
                        >
                            Tutup
                        </Button>
                    </ModalFooter>
                </ModalContent>
            </Modal>
        </Box>
    );
}